import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { StoredModel } from "./edit-model-dialog-utils"
import type { FairUseTier } from "@/api/admin/fair-use"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"

import { FEATURE_FLAGS_SCHEMA } from "./edit-model-dialog-utils"
import { ModelPlansField } from "./model-plans-field"
import { modelPlansEqual, normalizeModelPlans } from "./model-plans-utils"
import {
  useAdminModelPlansQuery,
  useRawEntityQuery,
  useSaveRawEntityMutation,
  useUpdateModelPlansMutation,
} from "../hooks/admin-queries"
import { useImageModelFamilies } from "../hooks/use-image-model-families"
import { KeyValueOverrides } from "./shared/key-value-overrides"
import { RawJsonTab } from "./shared/raw-json-tab"

type EditModelDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  modelId: string | null
}

/**
 * Edit dialog for an existing model entity.
 *
 * - **Form** tab surfaces the fields admins need most often (display_name,
 *   description, deployment, enabled, token limits, feature flags).
 * - **Raw JSON** tab shows the full stored model document — useful for
 *   schema fields not yet surfaced on the form (disclaimers, access policy
 *   nested structure, forward-compatible extensions).
 *
 * Both tabs write through the same ``PUT /admin/models/<id>/raw`` endpoint,
 * so the audit trail is uniform; Form saves set ``source="form"`` while
 * Raw saves set ``source="raw"``.
 */
export function EditModelDialog({ isOpen, onOpenChange, modelId }: EditModelDialogProps) {
  const { t } = useTranslation()
  const endpoint = modelId ? `/admin/models/${encodeURIComponent(modelId)}/raw` : null

  const {
    data: rawData,
    isLoading,
    refetch,
  } = useRawEntityQuery<StoredModel>(endpoint, isOpen && !!endpoint)

  const saveMutation = useSaveRawEntityMutation<StoredModel>({
    endpoint: endpoint ?? "",
    invalidateOnSuccess: [
      ["admin", "models"],
      ["admin", "embeddings"],
      ["admin", "specialized"],
    ],
  })

  const stored: StoredModel = useMemo(() => rawData?.entity ?? {}, [rawData])
  const hash = rawData?.hash ?? ""
  const modelType = stored.model_type ?? "chat"
  // Image entries carry an Azure family id in `model`, which decides the
  // quality levels, sizes and transparency support the backend offers.
  const isImageEntry = modelType === "specialized" && stored.type === "image"

  const [displayName, setDisplayName] = useState("")
  const [description, setDescription] = useState("")
  const [deployment, setDeployment] = useState("")
  const [enabled, setEnabled] = useState(true)
  const [inputTokenLimit, setInputTokenLimit] = useState<number | undefined>()
  const [outputTokenLimit, setOutputTokenLimit] = useState<number | undefined>()
  const [dimensions, setDimensions] = useState<number | undefined>()
  const [featureFlags, setFeatureFlags] = useState<Record<string, unknown>>({})
  const [imageFamily, setImageFamily] = useState("")
  const [resetSignal, setResetSignal] = useState(0)
  const [formError, setFormError] = useState<string | null>(null)
  const [plans, setPlans] = useState<FairUseTier[] | null>(null)

  const { families: imageFamilies } = useImageModelFamilies(isOpen && isImageEntry)

  // The plan allowlist is NOT part of the model entity: it lives on the
  // model's `system_models` override and is written through its own audited
  // endpoint, so it is read and saved separately from the raw document.
  const isChatModel = modelType === "chat"
  const { data: storedPlans } = useAdminModelPlansQuery(modelId ?? undefined, isOpen && isChatModel)
  const updatePlansMutation = useUpdateModelPlansMutation()

  useEffect(() => {
    setDisplayName(typeof stored.display_name === "string" ? stored.display_name : "")
    setDescription(typeof stored.description === "string" ? stored.description : "")
    setDeployment(typeof stored.deployment === "string" ? stored.deployment : "")
    setEnabled(stored.enabled ?? true)
    setInputTokenLimit(
      typeof stored.input_token_limit === "number" ? stored.input_token_limit : undefined
    )
    setOutputTokenLimit(
      typeof stored.output_token_limit === "number" ? stored.output_token_limit : undefined
    )
    setDimensions(typeof stored.dimensions === "number" ? stored.dimensions : undefined)
    setImageFamily(typeof stored.model === "string" ? stored.model : "")
    setFeatureFlags(
      stored.feature_flags && typeof stored.feature_flags === "object"
        ? { ...stored.feature_flags }
        : {}
    )
    setResetSignal((n) => n + 1)
  }, [stored])

  useEffect(() => {
    setPlans(normalizeModelPlans(storedPlans?.plans))
  }, [storedPlans])

  useEffect(() => {
    if (!isOpen) {
      setFormError(null)
    }
  }, [isOpen])

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!modelId || !endpoint || !hash) return
    setFormError(null)

    const next: StoredModel = {
      ...stored,
      display_name: displayName.trim(),
      description: description.trim(),
      deployment: deployment.trim(),
      enabled,
    }
    if (modelType === "chat") {
      if (typeof inputTokenLimit === "number") next.input_token_limit = inputTokenLimit
      if (typeof outputTokenLimit === "number") next.output_token_limit = outputTokenLimit
      next.feature_flags = featureFlags
    }
    if (modelType === "embedding" && typeof dimensions === "number") {
      next.dimensions = dimensions
    }
    if (isImageEntry && imageFamily) {
      next.model = imageFamily
    }

    try {
      await saveMutation.mutateAsync({
        entity: next as Record<string, unknown>,
        expectedHash: hash,
        reason: t("adminDashboard.modelEdit.reasonForm", {
          defaultValue: "Form-tab edit of model {{id}}",
          id: modelId,
        }),
        source: "form",
      })
      // Second write, deliberately AFTER the entity save and only when the
      // allowlist actually moved: it is a different document, a different
      // endpoint and its own audit action, and an unchanged dialog must not
      // log a "plans updated" event nobody made. The raw payload above carries
      // `plans` through untouched (it spreads `stored`), so the order is safe.
      if (isChatModel && !modelPlansEqual(plans, normalizeModelPlans(storedPlans?.plans))) {
        await updatePlansMutation.mutateAsync({ modelId, plans })
      }
      void refetch()
      toast.success(t("adminDashboard.modelEdit.toastSaved", { defaultValue: "Model updated" }))
      onOpenChange(false)
    } catch (err) {
      setFormError(
        err instanceof Error
          ? err.message
          : t("adminDashboard.modelEdit.errorSaveFailed", { defaultValue: "Save failed" })
      )
    }
  }

  const handleRawSave = async (next: unknown, reason: string) => {
    if (!modelId || !endpoint || !hash) return
    const parsed = (next || {}) as Record<string, unknown>
    try {
      await saveMutation.mutateAsync({
        entity: parsed,
        expectedHash: hash,
        reason,
        source: "raw",
      })
      void refetch()
      toast.success(
        t("adminDashboard.modelEdit.toastSavedRaw", { defaultValue: "Model saved (raw)" })
      )
      onOpenChange(false)
    } catch (err) {
      toast.error(
        t("adminDashboard.modelEdit.toastRawSaveFailed", { defaultValue: "Raw save failed" }),
        { description: err instanceof Error ? err.message : undefined }
      )
      throw err
    }
  }

  if (!modelId) return null

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle>
            {t("adminDashboard.modelEdit.title", { defaultValue: "Edit model" })}{" "}
            <code className="font-mono text-sm">{modelId}</code>
            {stored.display_name ? (
              <span className="text-muted-foreground text-sm font-normal">
                {" "}
                ({stored.display_name})
              </span>
            ) : null}
          </DialogTitle>
          <DialogDescription>
            {t("adminDashboard.modelEdit.description", {
              defaultValue:
                "Edit display name, description, deployment, and feature flags. The Raw tab shows the full stored model document.",
            })}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="form">
          <TabsList>
            <TabsTrigger value="form">
              {t("adminDashboard.modelEdit.tabForm", { defaultValue: "Form" })}
            </TabsTrigger>
            <TabsTrigger
              className="text-destructive data-[state=active]:text-destructive"
              value="raw"
            >
              {t("adminDashboard.modelEdit.tabRaw", { defaultValue: "Raw JSON" })}
            </TabsTrigger>
          </TabsList>

          <TabsContent className="mt-4 max-h-[60vh] overflow-y-auto" value="form">
            <form className="space-y-4" onSubmit={handleFormSubmit}>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t("adminDashboard.modelEdit.fieldId", { defaultValue: "ID" })}</Label>
                  <Input disabled className="font-mono text-xs" value={modelId} />
                </div>
                <div className="space-y-2">
                  <Label>{t("adminDashboard.modelEdit.fieldType", { defaultValue: "Type" })}</Label>
                  <Input disabled className="font-mono text-xs" value={modelType} />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="model-display-name">
                  {t("adminDashboard.modelEdit.fieldDisplayName", { defaultValue: "Display name" })}
                </Label>
                <Input
                  id="model-display-name"
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="model-description">
                  {t("adminDashboard.modelEdit.fieldDescription", { defaultValue: "Description" })}
                </Label>
                <Textarea
                  id="model-description"
                  rows={3}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="model-deployment">
                  {t("adminDashboard.modelEdit.fieldDeployment", {
                    defaultValue: "Deployment (Azure deployment name)",
                  })}
                </Label>
                <Input
                  className="font-mono text-xs"
                  id="model-deployment"
                  value={deployment}
                  onChange={(event) => setDeployment(event.target.value)}
                />
              </div>

              <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="model-enabled">
                    {t("adminDashboard.modelEdit.fieldEnabled", { defaultValue: "Enabled" })}
                  </Label>
                  <p className="text-muted-foreground text-xs">
                    {t("adminDashboard.modelEdit.fieldEnabledHelp", {
                      defaultValue:
                        "Disabled models stay in the catalog but can't be selected for new requests.",
                    })}
                  </p>
                </div>
                <Switch checked={enabled} id="model-enabled" onCheckedChange={setEnabled} />
              </div>

              {modelType === "chat" ? (
                <>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="model-input-limit">
                        {t("adminDashboard.modelEdit.fieldInputTokenLimit", {
                          defaultValue: "Input token limit",
                        })}
                      </Label>
                      <Input
                        id="model-input-limit"
                        type="number"
                        value={inputTokenLimit ?? ""}
                        onChange={(event) =>
                          setInputTokenLimit(
                            event.target.value === ""
                              ? undefined
                              : Number.parseInt(event.target.value, 10) || 0
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="model-output-limit">
                        {t("adminDashboard.modelEdit.fieldOutputTokenLimit", {
                          defaultValue: "Output token limit",
                        })}
                      </Label>
                      <Input
                        id="model-output-limit"
                        type="number"
                        value={outputTokenLimit ?? ""}
                        onChange={(event) =>
                          setOutputTokenLimit(
                            event.target.value === ""
                              ? undefined
                              : Number.parseInt(event.target.value, 10) || 0
                          )
                        }
                      />
                    </div>
                  </div>

                  <KeyValueOverrides
                    disabled={saveMutation.isPending}
                    schema={FEATURE_FLAGS_SCHEMA}
                    value={featureFlags}
                    description={t("adminDashboard.modelEdit.featureFlagsDescription", {
                      defaultValue:
                        "Capability markers read by the agent runtime. Add custom keys for forward-compatible flags.",
                    })}
                    label={t("adminDashboard.modelEdit.featureFlagsLabel", {
                      defaultValue: "Feature flags",
                    })}
                    onChange={setFeatureFlags}
                  />

                  <ModelPlansField
                    disabled={saveMutation.isPending || updatePlansMutation.isPending}
                    plans={plans}
                    onChange={setPlans}
                  />
                </>
              ) : null}

              {isImageEntry ? (
                <div className="space-y-2">
                  <Label htmlFor="model-image-family">
                    {t("adminDashboard.models.addDialog.imageModel", { defaultValue: "Model" })}
                  </Label>
                  <Select value={imageFamily} onValueChange={setImageFamily}>
                    <SelectTrigger id="model-image-family">
                      <SelectValue
                        placeholder={t("adminDashboard.models.addDialog.imageModelPlaceholder", {
                          defaultValue: "Select the Azure image model family",
                        })}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {imageFamilies.map((family) => (
                        <SelectItem key={family.id} value={family.id}>
                          <span>{family.label}</span>
                          <span className="text-muted-foreground ml-2 text-xs">
                            {family.tier === "fast"
                              ? t("adminDashboard.imageGeneration.tierFast", {
                                  defaultValue: "Fast",
                                })
                              : t("adminDashboard.imageGeneration.tierQuality", {
                                  defaultValue: "Highest quality",
                                })}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}

              {modelType === "embedding" ? (
                <div className="space-y-2">
                  <Label htmlFor="model-dimensions">
                    {t("adminDashboard.modelEdit.fieldDimensions", {
                      defaultValue: "Embedding dimensions",
                    })}
                  </Label>
                  <Input
                    id="model-dimensions"
                    type="number"
                    value={dimensions ?? ""}
                    onChange={(event) =>
                      setDimensions(
                        event.target.value === ""
                          ? undefined
                          : Number.parseInt(event.target.value, 10) || 0
                      )
                    }
                  />
                </div>
              ) : null}

              {formError ? <p className="text-destructive text-xs">{formError}</p> : null}

              <DialogFooter>
                <Button
                  disabled={saveMutation.isPending}
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  {t("adminDashboard.modelEdit.cancel", { defaultValue: "Cancel" })}
                </Button>
                <Button
                  type="submit"
                  disabled={
                    saveMutation.isPending || updatePlansMutation.isPending || isLoading || !hash
                  }
                >
                  {saveMutation.isPending ? (
                    <>
                      <Spinner />
                      {t("adminDashboard.modelEdit.saving", { defaultValue: "Saving…" })}
                    </>
                  ) : (
                    t("adminDashboard.modelEdit.save", { defaultValue: "Save" })
                  )}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>

          <TabsContent className="mt-4" value="raw">
            <RawJsonTab
              confirmToken={modelId}
              isPending={saveMutation.isPending || !hash}
              resetSignal={resetSignal}
              value={stored}
              resourceLabel={t("adminDashboard.modelEdit.rawResourceLabel", {
                defaultValue: "model {{id}}",
                id: modelId,
              })}
              onSave={handleRawSave}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
